<template>
  <PageView :page-reference="PAGE_REFERENCES.COMPANY_PROFILE" :current-object-type="default_object_type_obj">
    <template #header>
      <PagesTabs />
      <TripleMenu
        :set-edit-mode="setEditMode"
        :edit-mode-active="edit_mode_active"
        :quick-actions="top_right_menu_quick_actions"
        :components-actions="top_right_menu_components_actions"
        :current-template="current_template"
        :page-reference="PAGE_REFERENCES.COMPANY_PROFILE" />
      <PageBreadCrumb
        :page-reference="PAGE_REFERENCES.COMPANY_PROFILE"
        :is-page-title-hidden="true"
        :page-title="$route.params?.page ? $route.params?.page : `COMPANY_PROFILE`"
        :page-bread-crumb="bread_crumbs" />
    </template>
    <template #body>
      <LayoutContainer
        v-model:current-template="current_template"
        :set-edit-mode="setEditMode"
        :edit-mode-active="edit_mode_active"
        class="mt-3 !ml-6 !h-[calc(100vh-180px)]"
        :page-reference="PAGE_REFERENCES.COMPANY_PROFILE" />
    </template>
  </PageView>
  <Modal v-model:is-open="show_confirm_unsave_layout">
    <div class="sm:flex sm:items-start">
      <div class="flex items-center justify-center flex-shrink-0 w-12 h-12 mx-auto bg-red-100 rounded-full sm:mx-0 sm:h-10 sm:w-10">
        <CircleHelp class="w-8 h-8 text-red-600" aria-hidden="true" />
      </div>
      <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
        <div as="h3" class="text-lg font-medium leading-6 text-gray-900">
          {{ $t("Unsaved Layout Changes") }}
        </div>
        <div class="flex flex-col mt-2">
          <p class="font-semibold text-md">
            {{ $t("Do you really want to exit this page? Any unsaved changes will be disregarded.") }}
          </p>
        </div>
      </div>
    </div>
    <div class="gap-2 mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
      <button
        type="button"
        class="inline-flex justify-center w-full px-4 py-2 mt-3 text-base font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-dark sm:mt-0 sm:w-auto sm:text-sm"
        @click="handleModalResponse('no')">
        {{ $t("Cancel") }}
      </button>
      <button
        type="button"
        class="inline-flex justify-center w-full px-4 py-2 text-base font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
        @click="handleModalResponse('yes')">
        {{ $t("Yes") }}
      </button>
    </div>
  </Modal>
</template>
<script>
import TripleMenu from "@/components/shared/TripleMenu.vue"
import PagesTabs from "@/components/shared/PagesTabs"
import PageView from "@/components/shared/pages/PageView.vue"
import { VIEW_TYPES } from "@/store/constants/references"
import { PAGE_REFERENCES } from "@/store/constants/references"
import LayoutContainer from "@/components/shared/pages/LayoutContainer.vue"
import PageBreadCrumb from "@/components/shared/pages/PageBreadCrumb.vue"
import ModuleIcon from "@/components/shared/icons/ModuleIcon.vue"
import { utils } from "@/store/helpers"
import {
  Plus,
  User,
  Users,
  ClipboardList,
  Mail,
  Folder,
  TrendingUp,
  MessagesSquare,
  FileDown,
  ArrowLeft,
  List,
  Tag,
  CircleHelp,
  ChartNoAxesColumnDecreasing
} from "lucide-vue-next"
import { ALL_EMITTERS } from "@/store/constants/all_emitters"
import Modal from "@/components/shared/Modal.vue"
import { markRaw } from "vue"

export default {
  components: {
    Modal,
    PageView,
    CircleHelp,
    // ObjectsView,
    LayoutContainer,
    TripleMenu,
    PagesTabs,
    PageBreadCrumb,
    // eslint-disable-next-line vue/no-unused-components
    ArrowLeft: markRaw(ArrowLeft)
  },
  inject: ["isModuleActivated"],
  async beforeRouteLeave(to, from, next) {
    if (this?.edit_mode_active) {
      const ignore_changes = await this.ignoreLayoutChanges()
      this.edit_mode_active = ignore_changes != "yes"
      if (ignore_changes == "yes") {
        next()
      } else {
        next(false)
      }
    } else {
      next(true)
    }
  },
  data() {
    return {
      PAGE_REFERENCES,
      current_template: null,
      default_object_type_obj: { type: "default" },
      edit_mode_active: false,
      show_confirm_unsave_layout: false,
      ignore_changes: false
    }
  },
  computed: {
    bread_crumbs() {
      if (!this.$store.state.ui.currentCompany) {
        return []
      }
      return [
        {
          id: 1,
          label: " ",
          component: ArrowLeft,
          hide_arrow: true,
          is_component: true,
          onClick: () => {
            this.$router.go(-1)
          }
        },
        {
          id: 2,
          label: utils.getObjectTypeByType(this.$store.state.ui.currentCompany?.type).plural_label,
          onClick: () => {
            this.$router.push(`/portfolio/${this.$store.state.ui.currentCompany.type}`)
          }
        },
        {
          id: 3,
          label: utils.Record(this.$store.state.ui.currentCompany).name
        },
        {
          id: 4,
          label: utils.isUUID(this.$route.params.page) && this.current_template ? this.current_template.name : this.$route.params.page
        }
      ]
    },
    top_right_menu_quick_actions() {
      return [
        {
          name: this.$t("Add Contact"),
          action: () => {
            const metrics_obj = {
              object_type: "contact",
              page_reference: PAGE_REFERENCES.CONTACT_PROFILE,
              view_type: VIEW_TYPES.FORM_CREATE
            }
            this.$emitter.emit(ALL_EMITTERS.ADD_NEW_RECORD, metrics_obj)
          },
          icon: Plus
        },
        {
          name: this.$t("Add Department"),
          action: () => {
            const metrics_obj = {
              object_type: "department",
              page_reference: PAGE_REFERENCES.COMPANY_PROFILE,
              view_type: VIEW_TYPES.FORM_CREATE
            }
            this.$emitter.emit(ALL_EMITTERS.ADD_NEW_RECORD, metrics_obj)
          },
          icon: Plus
        },
        {
          name: this.$t("Add Opportunity"),
          action: () => {
            const metrics_obj = {
              object_type: "opportunity",
              page_reference: PAGE_REFERENCES.COMPANY_PROFILE,
              view_type: VIEW_TYPES.FORM_CREATE
            }
            this.$emitter.emit(ALL_EMITTERS.ADD_NEW_RECORD, metrics_obj)
          },
          icon: Plus
        },
        {
          name: this.$t("Create a Group"),
          action: () => {
            this.$emitter.emit(ALL_EMITTERS.CREATE_NEW_GROUP, "company")
          },
          disabled: false,
          icon: Folder
        },
        {
          name: this.$t("Create a Plan"),
          action: () => {
            this.$emitter.emit(ALL_EMITTERS.CREATE_PLAN, {
              record: this.$store.state.ui.currentCompany
            })
          },
          disabled: false,
          icon: List
        },
        {
          name: this.$t("Create a Goal"),
          action: () => {
            this.$emitter.emit(ALL_EMITTERS.CREATE_GOAL, {
              record: this.$store.state.ui.currentCompany
            })
          },
          disabled: false,
          icon: TrendingUp
        },
        {
          name: this.$t("Create a Task"),
          action: () => {
            this.$emitter.emit(ALL_EMITTERS.CREATE_TASK, {
              record: this.$store.state.ui.currentCompany
            })
          },
          disabled: false,
          icon: ClipboardList
        },
        {
          name: this.$t("Create an Interaction"),
          action: () => {
            this.$emitter.emit(ALL_EMITTERS.CREATE_INTERACTION, {
              record: this.$store.state.ui.currentCompany
            })
          },
          disabled: false,
          icon: MessagesSquare
        },
        {
          name: this.$t("Send Email"),
          action: () => {
            this.$emitter.emit(ALL_EMITTERS.SEND_EMAIL, {
              record: this.$store.state.ui.currentCompany
            })
          },
          disabled: false,
          icon: Mail
        },
        {
          name: this.$t("Assign Owner"),
          action: () => {
            this.$emitter.emit(ALL_EMITTERS.CREATE_OWNER, {
              record: this.$store.state.ui.currentCompany
            })
          },
          disabled: false,
          icon: User
        },
        {
          name: this.$t("Assign Co-owners"),
          action: () => {
            this.$emitter.emit(ALL_EMITTERS.CHANGE_CO_OWNERS, {
              record: this.$store.state.ui.currentCompany
            })
          },
          disabled: false,
          icon: Users
        },
        {
          name: this.$t("Generate NPS"),
          action: () => {
            this.$emitter.emit(ALL_EMITTERS.GENERATE_EXTERNAL_SCORE, {
              records: [this.$store.state.ui.currentCompany]
            })
          },
          disabled: false,
          icon: FileDown
        },
        {
          name: this.$t("Assign Tag"),
          action: () => {
            this.$emitter.emit(ALL_EMITTERS.ASSIGN_TAGS, {
              record: this.$store.state.ui.currentCompany,
              object_type: this.$route.params.object_type
            })
          },
          disabled: false,
          icon: Tag
        },
        {
          name: this.$t("Create a Custom Metric"),
          action: () => {
            this.$emitter.emit(ALL_EMITTERS.CREATE_CUSTOM_METRIC, {
              object_type: this.$route.params.object_type
            })
          },
          disabled: false,
          icon: ChartNoAxesColumnDecreasing
        }
      ]
    },
    top_right_menu_components_actions() {
      return [
        {
          name: this.$t("Edit Layout"),
          action: () => {
            this.$emitter.emit(ALL_EMITTERS.EDIT_LAYOUT, {
              page_reference: PAGE_REFERENCES.COMPANY_PROFILE,
              page_name: this.$route.params.page
            })
          },
          disabled: false,
          keyBoardShortcut: "I",
          icon: ModuleIcon
        },
        {
          name: this.$t("Add/Remove Components"),
          action: () => {
            this.$emitter.emit(ALL_EMITTERS.ADD_NEW_COMPONENT, {
              page_reference: PAGE_REFERENCES.COMPANY_PROFILE,
              current_template: this.current_template
            })
          },
          disabled: false,
          keyBoardShortcut: "U",
          icon: ModuleIcon
        },
        {
          name: this.current_template?.is_static ? this.$t("Reset Page") : this.$t("Delete Page"),
          action: () => {
            this.$emitter.emit(ALL_EMITTERS.DELETE_PAGE, this.current_template)
          },
          icon: ModuleIcon
        }
      ]
    }
  },
  activated() {
    // Retrieve the company profile pages from the store and filter out hidden pages
    const { pages_layout } = this.$store.state.portfolio
    const visible_pages = pages_layout["company_profile"]?.filter((page) => !page.is_hidden) || []

    // If no page is currently selected and there are visible pages available
    if (!this.$route.params.page && visible_pages.length) {
      // Get the first visible page ID
      const first_visible_page_id = visible_pages[0].id

      // Build the correct path by appending the page ID, adding a slash if necessary
      const path = `${this.$route.path.replace(/\/$/, "")}/${first_visible_page_id}`

      // Replace the current route with the new path
      this.$router.replace(path)
    }
  },
  unmounted() {
    // Clear breadcrumb
    this.$store.dispatch("ui/setCurrentCompanyBreadcrumb", [])
    // remove current company from store
    this.$store.dispatch("ui/setCurrentCompany", null)
  },
  mounted() {
    this.show_confirm_unsave_layout = false
  },
  methods: {
    ignoreLayoutChanges() {
      return new Promise((resolve) => {
        this.show_confirm_unsave_layout = true
        // We Use a watcher to detect when the user has made a decision
        const unwatch = this.$watch("ignore_changes", (newValue) => {
          if (newValue != undefined) {
            this.show_confirm_unsave_layout = false
            // give the modal time to close
            setTimeout(() => {
              resolve(newValue)
              // Remove the watcher once the decision has been made
              unwatch()
              // Reset the value to undefined super important
              this.ignore_changes = undefined
            }, 300)
          }
        })
      })
    },
    handleModalResponse(answer) {
      this.ignore_changes = answer
    },
    setEditMode(value) {
      this.edit_mode_active = value
    }
  }
}
</script>
